// Núcleo da validação (§14.7): tipos, limites e as checagens do envelope comum que todo validador usa. Módulo folha:
// validate.ts e os validate.*.ts do acervo e dos insights (P-26, P-27) importam daqui, e este não importa nenhum deles
// (sem ciclo). Funções puras: nada de relógio do aparelho (só serverNowMs, do cabeçalho Date da resposta).
import type * as z from 'zod/mini';
import { type AggregateScope, type ElectionYear, type FeedHeader, REGION_CODES, tMin, UF_CODES } from './base';
import type { Snapshot } from './snapshot';
import type { CandidateList, Geography } from './static';

export type ValidationCode =
  | 'E_JSON'
  | 'E_SIZE'
  | 'E_SCHEMA'
  | 'E_VERSION'
  | 'E_ELECTION'
  | 'E_CLOCK'
  | 'E_RANGE'
  | 'E_SUM'
  | 'E_STATUS'
  | 'E_PLACES'
  | 'E_RECON'
  | 'E_DECISION'
  | 'E_REGISTRY'
  | 'E_ORIGIN'
  | 'E_SEQ';
/**
 * Campo opcional descartado sem recusar o arquivo. Os três primeiros são do feed ao vivo; os demais são os nomes do
 * acervo (P-26) e dos insights (P-27), que validate.replay.ts, validate.sections.ts e validate.insights.ts registram.
 * [R] rascunho a fechar com o SINAL do motor: 'score', 'voting_day', 'narration', 'curated_context' e 'parts' nomeiam
 * blocos ainda em rascunho no contrato.
 */
export type DroppedField =
  | 'ops.notice'
  | 'probability'
  | 'ops.reload_build'
  // P-26: trilhas de chance do manifesto e dos quadros (I-25)
  | 'model_tracks.p'
  | 'model_tracks.ps'
  | 'model_tracks.pr'
  | 'frames.p'
  | 'frames.ps'
  | 'frames.pr'
  | 'frames.pu'
  | 'frames.pus'
  | 'frames.pur'
  // P-26: blocos opcionais das seções e da comparação
  | 'transfer'
  | 'voting_day'
  | 'score'
  // P-27: relatório de IA (I-28); voz e contexto fora do regime live, cartão ignorado do índice e índices por UF (I-26)
  | 'report'
  | 'narration'
  | 'curated_context'
  | 'cards'
  | 'parts'
  // P-32: comentário ao vivo que fere uma invariante (sai sozinho) e P-33: lugar mal formado em ops.held
  | 'comments'
  | 'ops.held'
  // P-40 (A2): uma família da A2 do índice da análise que falha cai sozinha (o índice da A1 segue)
  | 'families.pace'
  | 'families.pause'
  | 'families.burst'
  | 'families.open'
  | 'families.close'
  | 'families.machine';
export interface ValidationIssue {
  code: ValidationCode;
  path: string;
  detail: string;
}
export type Validated<T> =
  | { ok: true; value: T; warnings: ValidationCode[]; dropped: DroppedField[] }
  | { ok: false; errors: ValidationIssue[] };

/** O que a validação precisa da configuração (AppConfig, §2.5). */
export interface ValidationConfig {
  round: 1 | 2;
  election_date: string;
  mode: 'live' | 'simulation';
  /** Ano esperado no cabeçalho (I-02). Ausente = 2026; o alvo replay passa o ano da noite, com o turno e a data dela. */
  election_year?: ElectionYear;
}
export interface ValidationContext {
  config: ValidationConfig;
  /** No app, sempre presente. Só o contracts:check sem cadastro passa null: I-10 confere só o par do próprio arquivo. */
  candidates: CandidateList | null;
  geography: Geography | null;
  current: Snapshot | null;
  isProductionBuild: boolean;
  /** Cabeçalho HTTP Date da resposta (exposto, §14.10); null sem ele. Nunca Date.now() do aparelho. */
  serverNowMs: number | null;
  /** Alvo do build (I-22): 'replay' = site do acervo, que só aceita origin "archive". Ausente = 'live'. */
  target?: 'live' | 'replay';
  /**
   * Lint do relatório de IA (I-28): devolve as regras violadas por um parágrafo; lista vazia = passa. Entra por injeção
   * porque contracts/ só importa zod: a camada app/ e os scripts passam o vocabulário de src/copy. Falha fechada:
   * ausente ou null, o relatório não tem como ser conferido e é descartado (fica o cartão, com os números).
   */
  reportLint?: ((text: string) => string[]) | null;
}

export const LIMITS = {
  maxResponseBytes: 6_000_000,
  clockSkewS: 120,
  futureToleranceS: 600,
  tMinRange: [0, 2880],
  sumTolAbs: 10,
  sumTolRel: 0.005,
  turnoutTolAbs: 10,
  turnoutTolRel: 0.01,
  nationalAheadTolRel: 0.25,
  electorateTolRel: 0.01,
  noticeMaxChars: 200,
  publicReasonMaxChars: 120,
  pollHintBoundsS: [5, 300],
} as const;

export class Issues {
  readonly list: ValidationIssue[] = [];
  add(code: ValidationCode, path: string, detail: string): void {
    this.list.push({ code, path, detail });
  }
  get empty(): boolean {
    return this.list.length === 0;
  }
}

export function zodIssues(error: z.core.$ZodError, issues: Issues): void {
  for (const i of error.issues.slice(0, 20)) issues.add('E_SCHEMA', i.path.join('.'), i.message);
}

/**
 * I-01 (schema e versão) antes da estrutura, para devolver E_SCHEMA/E_VERSION e não um erro genérico. A mensagem diz
 * só o esperado, nunca o valor recebido: o arquivo vem da rede, e pôr o valor num texto com String() lançava quando
 * ele era um objeto sem toString chamável (`{"schema":{"toString":1}}`), no meio do ciclo de busca.
 */
export function checkSchemaName(raw: unknown, expected: string, issues: Issues): boolean {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    issues.add('E_SCHEMA', '', 'não é um objeto');
    return false;
  }
  const r = raw as Record<string, unknown>;
  if (r.schema !== expected) {
    issues.add('E_SCHEMA', 'schema', `esperado ${expected}`);
    return false;
  }
  if (r.schema_version !== 1) {
    issues.add('E_VERSION', 'schema_version', 'esperado 1');
    return false;
  }
  return true;
}

const ms = (iso: string) => Date.parse(iso);

/** I-02, I-04, I-05, I-17, I-22: eleição, relógios e origem do envelope comum. */
export function checkHeader(h: FeedHeader<string>, ctx: ValidationContext, issues: Issues): void {
  // I-02: o zod aceita os anos do acervo; aqui vale o da configuração (2026 no site ao vivo, o da noite no replay).
  const e = h.election;
  if (
    e.year !== (ctx.config.election_year ?? 2026) ||
    e.round !== ctx.config.round ||
    e.date !== ctx.config.election_date
  ) {
    issues.add('E_ELECTION', 'election', `${e.year}/${e.round}/${e.date}`);
  }
  // I-22 antes da I-17: o site do replay também é produção (DR-30), e a I-17 sozinha recusaria todo o acervo. No alvo
  // replay só vale "archive"; fora dele, "archive" nunca vale e a I-17 segue como estava.
  const replay = ctx.target === 'replay';
  if (replay ? h.origin !== 'archive' : h.origin === 'archive') {
    issues.add('E_ORIGIN', 'origin', `origem "${h.origin}" não vale neste alvo (I-22)`);
  } else if (!replay && ctx.isProductionBuild && ctx.config.mode === 'live' && h.origin !== 'tse') {
    issues.add('E_ORIGIN', 'origin', `produção só aceita origin "tse" (veio "${h.origin}")`);
  }
  const skew = LIMITS.clockSkewS * 1000;
  const c = h.clocks;
  if (c.tse_ht !== null && ms(c.tse_ht) > ms(c.collected_at) + skew) {
    issues.add('E_CLOCK', 'clocks.tse_ht', 'tse_ht depois de collected_at');
  }
  if (ms(c.collected_at) > ms(c.published_at) + skew) {
    issues.add('E_CLOCK', 'clocks.collected_at', 'collected_at depois de published_at');
  }
  // P-18 (10/10): "no futuro" só vale para o feed do TSE. No `replay` e na `simulation`, o `published_at` é a hora da
  // noite gravada (deslocada para a data da eleição, P-16), não a do servidor; a produção só aceita `tse` (acima).
  if (
    h.origin === 'tse' &&
    ctx.serverNowMs !== null &&
    ms(c.published_at) > ctx.serverNowMs + LIMITS.futureToleranceS * 1000
  ) {
    issues.add('E_CLOCK', 'clocks.published_at', 'published_at mais de 10 min à frente do Date do servidor');
  }
  if (c.tse_ht !== null) checkTMin(c.tse_ht, ctx, 'clocks.tse_ht', issues);
}

export function checkTMin(iso: string, ctx: ValidationContext, path: string, issues: Issues): void {
  const t = tMin(iso, ctx.config.election_date);
  if (!(t >= LIMITS.tMinRange[0] && t <= LIMITS.tMinRange[1]))
    issues.add('E_CLOCK', path, `t_min ${t} fora de [0, 2880]`);
}

/**
 * Escopo agregado (F-9): o código é o do nível (RegionCode em REGION, UfCode em UF) e não existe em BR. 'SE' é UF e
 * região, e só o nível diz qual. AggregateScopeSchema confere a estrutura; quem valida o arquivo chama esta função
 * (manifesto e comparação do acervo) e recusa com E_PLACES. Devolve o motivo ou null.
 */
export function aggregateScopeProblem({ level, code }: AggregateScope): string | null {
  if (level === 'BR') return code === undefined ? null : 'BR não tem código';
  const codes: readonly string[] = level === 'REGION' ? REGION_CODES : UF_CODES;
  return code !== undefined && codes.includes(code) ? null : `"${String(code)}" não é código de ${level}`;
}

// I-27: nomes de campo que são do motor e nunca do contrato público (DR-33): identificador da versão publicada,
// severidade operacional, z bruto, p-valor bruto, classe técnica, regra do detector e as chaves do bloco `interno` do
// registro do motor (`camada_a`, `alt09`, `publicavel`, `namespace`). A lista cobre os nomes do motor em português e a
// tradução deles (o projetor do feed traduz os nomes): `z_nulo_empirico`, `robustez_z`, `z_score`, `p`, `p_valor`,
// `regra`, `severidade_operacional`, `classe_tecnica`. Confere o nome inteiro e, nas chaves com ponto (as de `numbers`:
// 'rar.z_bruto'), cada trecho: é a segunda trava do motor (`_RE_TRECHO_INTERNO`, projecao.py), repetida aqui.
const INTERNAL_KEY_NAMES = [
  'release[a-z0-9_]*',
  '(?:[a-z0-9]+_)*sever(?:ity|idade)(?:_[a-z0-9]+)*',
  'z|z_[a-z0-9_]+|[a-z0-9_]+_z|zscore|zraw',
  'p|p_?valor|p_?value',
  'classe?|classe?_tecnic[ao]|technical_class',
  'regra|rule',
  'interno|internal|namespace|camada_a|alt_?09|publicavel',
];
const INTERNAL_KEYS_MAX = 20;
// A expressão é montada na primeira chamada, e não no topo do módulo: este arquivo está no caminho crítico do site ao
// vivo, que não chama a I-27, e uma chamada no topo não sairia na poda de código morto (B01, §15.7).
let internalKeyRe: RegExp | undefined;
/** I-27 num nome de chave: o nome inteiro ou um trecho dele entre pontos é interno. */
function isInternalKey(name: string): boolean {
  internalKeyRe ??= new RegExp(`^(?:${INTERNAL_KEY_NAMES.join('|')})$`, 'i');
  const re = internalKeyRe;
  return name.split('.').some((part) => re.test(part));
}

interface WalkFrame {
  node: object;
  /** Chaves do objeto; null numa lista. */
  keys: string[] | null;
  next: number;
  /** Como o pai chama este nó: chave ou índice. */
  name: string | number;
  parent: WalkFrame | null;
}

function walkPath(frame: WalkFrame, last: string): string {
  const parts: Array<string | number> = [last];
  for (let f: WalkFrame | null = frame; f?.parent; f = f.parent) parts.push(f.name);
  return parts.reverse().join('.');
}

/**
 * I-27: caminhos ("signals.0.evidence.z_raw", "numbers.rar.z_bruto") das chaves internas, em qualquer profundidade e
 * na ordem do documento; lista vazia = nada interno. Recebe o JSON como veio, antes do zod, que descarta os campos que
 * não conhece. É a única exceção ao "campo extra é aceito" (P11), e só nos arquivos que a aplicam (sinal e índice de
 * insights, seções e comparação do acervo): quem chama recusa com E_SCHEMA. O bloco nacional do acervo não a aplica:
 * lá, `p` é a trilha publicada da chance. Percorre sem recursão e para nos 20 primeiros achados, porque o arquivo vem
 * da rede e pode ser fundo ou enorme.
 */
export function findInternalKeys(raw: unknown): string[] {
  const found: string[] = [];
  const open = (node: object, name: string | number, parent: WalkFrame | null): WalkFrame => ({
    node,
    keys: Array.isArray(node) ? null : Object.keys(node),
    next: 0,
    name,
    parent,
  });
  let frame = typeof raw === 'object' && raw !== null ? open(raw, '', null) : null;
  while (frame !== null && found.length < INTERNAL_KEYS_MAX) {
    const { node, keys } = frame;
    if (frame.next >= (keys ?? (node as unknown[])).length) {
      frame = frame.parent;
      continue;
    }
    const i = frame.next++;
    const name = keys ? (keys[i] as string) : i;
    const value = (node as Record<string | number, unknown>)[name];
    if (typeof name === 'string' && isInternalKey(name)) found.push(walkPath(frame, name));
    if (typeof value === 'object' && value !== null) frame = open(value, name, frame);
  }
  return found;
}
